import { useMemo } from "react";
import { catalogueEntryOf } from "../../catalogue/types";
import type { LegendEntry } from "../../catalogue/types";
import { useCatalogue, useLabels, useLayers } from "../hooks";
import { EmptyState } from "../primitives/EmptyState";
import { Swatch } from "./Swatch";

export type LegendProps = {
  entries?: LegendEntry[] | null;
  compact?: boolean;
  className?: string;
};

type LegendSection = { id: string; title: string; entries: LegendEntry[] };

export function Legend({ entries = null, compact = false, className }: LegendProps) {
  const labels = useLabels();
  const catalogue = useCatalogue();
  const { list } = useLayers();

  const sections = useMemo<LegendSection[]>(() => {
    if (entries !== null) return [{ id: "own", title: "", entries }];
    if (catalogue === null) return [];

    const result: LegendSection[] = [];

    for (const snapshot of list) {
      if (!snapshot.selected || snapshot.root === "maps") continue;

      const entry = catalogueEntryOf(catalogue, snapshot);
      const own = entry?.legend ?? null;

      if (own !== null && own.length > 0) {
        result.push({ id: snapshot.id, title: snapshot.title, entries: own });
        continue;
      }

      if (entry !== null && entry.style !== null) {
        result.push({
          id: snapshot.id,
          title: snapshot.title,
          entries: [{ label: snapshot.title, color: entry.style.pen?.color ?? entry.style.brush?.color ?? null, fillColor: entry.style.brush?.color ?? null, iconUrl: entry.style.icon?.url ?? null }]
        });
      }
    }

    return result;
  }, [entries, catalogue, list]);

  if (sections.length === 0) {
    if (compact) return null;

    return <EmptyState icon="legend" title={labels.noLayers} className={className} />;
  }

  return (
    <div className={["fxmap-legend", compact ? "fxmap-legend-compact" : "", className ?? ""].filter(Boolean).join(" ")} data-fxmap="legend" role="list" aria-label={labels.legend}>
      {sections.map(section => (
        <div key={section.id} className="fxmap-legend-section" role="listitem">
          {section.title.length > 0 && !compact ? <div className="fxmap-legend-title fxmap-display fxmap-truncate">{section.title}</div> : null}
          <ul className="fxmap-legend-list">
            {section.entries.map((legend, index) => (
              <li key={`${legend.label}-${index}`} className="fxmap-legend-row">
                <Swatch legend={legend} />
                <span className="fxmap-legend-label fxmap-truncate">{legend.label}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
